import React, {useState, useEffect, useCallback} from 'react';
import {useParams, useNavigate, useLocation} from 'react-router-dom';
import {useToast} from '../components/Toast';
import {getJWTToken} from '../lib/auth-token';
import {useAppDispatch, useAppSelector} from '../store/hooks';
import {TranscriptionJob} from '../domain/types';
import {projectDataSelector} from '../store/selectors/projects.selectors';
import {
  fetchQueue,
  startQueueProcessing as startQueueProcessingThunk,
  stopQueueProcessing as stopQueueProcessingThunk,
  addToQueue,
  removeFromQueue,
} from '../store/slices/queueThunks';
import {
  setLiveOutput,
  clearCompletionStatus,
  setProcessingOriginPath,
} from '../store/slices/queueSlice';
import {setSelectedTranscription} from '../store/slices/transcriptionsSlice';
import {fetchTranscriptions} from '../store/slices/transcriptionsThunks';

const TranscriptionQueuePage: React.FC = () => {
  const {projectId} = useParams<{ projectId?: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useAppDispatch();

    // Get queue state from Redux
  const {jobs: allJobs, isProcessing, liveOutput, queueCompletionStatus} = useAppSelector(state => state.queue);
  const projects = useAppSelector(projectDataSelector);
  const {transcriptions} = useAppSelector(state => state.transcriptions);

    // Filter jobs by project
  const queue = projectId
    ? allJobs.filter(job => job.projectId === projectId)
    : allJobs.filter(job => !job.projectId);

    // Get project name from Redux
  const projectName = projectId ? (projects.find(p => p.id === projectId)?.name ?? '') : '';
  const [selectedJob, setSelectedJob] = useState<TranscriptionJob | null>(null);
  const [showLogsModal, setShowLogsModal] = useState(false);
  const [ellipsisCount, setEllipsisCount] = useState(1);
  const {showToast} = useToast();
  const prevJobsRef = React.useRef<Map<string, string>>(new Map());

  const pendingCount = queue.filter(j => j.status.toLowerCase() === 'pending').length;
  const processingCount = queue.filter(j => j.status.toLowerCase() === 'processing').length;
  const completedCount = queue.filter(j => j.status.toLowerCase() === 'completed').length;
  const failedCount = queue.filter(j => j.status.toLowerCase() === 'failed').length;

  const loadQueue = useCallback(async () => {
    await dispatch(fetchQueue());
  }, [dispatch]);

  useEffect(() => {
    loadQueue();
  }, [loadQueue]);

  // Monitor queue changes and show toasts when job status transitions
  useEffect(() => {
    queue.forEach((job) => {
      const prevStatus = prevJobsRef.current.get(job.id);
      const jobStatusLower = job.status.toLowerCase();

      // Show toast when job transitions to completed
      if (prevStatus !== 'completed' && jobStatusLower === 'completed') {
        showToast(`CSV uploaded for ${formatFileName(job.audioPath)}`, 'success');
        dispatch(setLiveOutput(`✅ Completed: ${formatFileName(job.audioPath)}`));
      }

      // Show toast when job transitions to failed
      if (prevStatus !== 'failed' && jobStatusLower === 'failed') {
        showToast(`Failed to transcribe ${formatFileName(job.audioPath)}`, 'error');
        dispatch(setLiveOutput(`❌ Failed: ${formatFileName(job.audioPath)}`));
      }

      // Update processing status in live output
      if (prevStatus !== 'processing' && jobStatusLower === 'processing') {
        dispatch(setLiveOutput(`Processing: ${formatFileName(job.audioPath)}`));
      }

      // Update the previous status
      prevJobsRef.current.set(job.id, jobStatusLower);
    });
  }, [queue, dispatch, showToast]);

  // Check if queue is complete
  useEffect(() => {
    if (queueCompletionStatus) {
      dispatch(stopQueueProcessingThunk());
    }
  }, [queueCompletionStatus, dispatch]);

  // Stop processing if all jobs are no longer pending or processing
  useEffect(() => {
    if (isProcessing && pendingCount === 0 && processingCount === 0 && queue.length > 0) {
      dispatch(stopQueueProcessingThunk());
      dispatch(setProcessingOriginPath(null));
    }
  }, [pendingCount, processingCount, isProcessing, queue.length, dispatch]);

    // Animate ellipsis when processing
  useEffect(() => {
    if (processingCount > 0) {
      const interval = setInterval(() => {
        setEllipsisCount(prev => (prev % 4) + 1);
      }, 500);
      return () => clearInterval(interval);
    }
    setEllipsisCount(1);

  }, [processingCount]);

  // Poll for queue updates when processing is active
  useEffect(() => {
    if (isProcessing) {
      const pollInterval = setInterval(() => {
        loadQueue();
      }, 2000); // Poll every 2 seconds while processing
      return () => clearInterval(pollInterval);
    }
  }, [isProcessing, loadQueue]);

  const handleToggleQueueProcessing = async () => {
    if (isProcessing) {
            // Pause processing
      await dispatch(stopQueueProcessingThunk());
      dispatch(setProcessingOriginPath(null));
    } else {
            // Start processing
      dispatch(setProcessingOriginPath(location.pathname));
      try {
                // Get JWT token for uploading to Worker API
        const jwt = await getJWTToken();
        await dispatch(startQueueProcessingThunk(jwt || undefined));
      } catch (error) {
        console.error('Queue processing error:', error);
        dispatch(setProcessingOriginPath(null));
      }
    }
  };

  const handleRemoveJob = async (jobId: string) => {
        // Get the job details for the toast
    const job = queue.find(j => j.id === jobId);
    const fileName = job ? formatFileName(job.audioPath) : 'Unknown file';

    try {
      await dispatch(removeFromQueue(jobId));

            // Show success toast
      showToast(`Removed "${fileName}" from queue`, 'success');

      loadQueue();
    } catch {
            // Show error toast
      showToast(`Failed to remove "${fileName}" from queue`, 'error');
    }
  };

  const handleRequeueJob = async (job: TranscriptionJob) => {
    const fileName = formatFileName(job.audioPath);

    try {
            // Add a new job with the same details
      await dispatch(addToQueue({
        audioPath: job.audioPath,
        csvFileName: job.csvFileName,
        projectId: job.projectId || null,
      }));

            // Remove the old failed job
      await dispatch(removeFromQueue(job.id));

            // Show success toast
      showToast(`Re-queued "${fileName}" for processing`, 'success');

      loadQueue();
    } catch {
            // Show error toast
      showToast(`Failed to requeue "${fileName}"`, 'error');
    }
  };

  const handleClearCompleted = async () => {
        // Check if there are any completed jobs
    if (completedCount === 0) {
      return; // Do nothing if no completed jobs
    }

    // Clearing completed jobs belongs to the server-side queue, which does not exist yet.
    // See docs/BACKEND_REQUESTS.md — "Server-side transcription jobs".
    showToast('The transcription queue is not available yet.', 'warning');
    dispatch(clearCompletionStatus());
  };

  const handleNavigateToConvert = async (job: TranscriptionJob) => {
        // Find the matching transcription in the list by comparing audio paths
    const matchingTranscription = transcriptions.find(t =>
      t.audioPath && job.audioPath && t.audioPath === job.audioPath
    );

    if (matchingTranscription) {
      // Auto-select the matching transcription
      dispatch(setSelectedTranscription(matchingTranscription));
    } else if (projectId) {
      // If not found in current transcriptions, refresh the list and try to find it
      try {
        await dispatch(fetchTranscriptions(projectId));
        // Note: The fetched transcriptions will update the state after dispatch completes
        // For now, just navigate and let user select manually if needed
      } catch (error) {
        console.error('Failed to fetch transcriptions:', error);
      }
    }

    if (projectId) {
      navigate(`/projects/${projectId}/workspace`);
    } else {
      navigate('/workspace');
    }
  };

  const handleJobClick = (job: TranscriptionJob) => {
        // Only show logs for completed or failed jobs
    if (job.status.toLowerCase() === 'completed' || job.status.toLowerCase() === 'failed') {
      setSelectedJob(job);
      setShowLogsModal(true);
    }
  };

  const closeLogsModal = () => {
    setShowLogsModal(false);
    setSelectedJob(null);
  };

  const formatTime = (seconds: number | null) => {
    if (seconds === null) return '-';
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}m ${secs}s`;
  };

  const formatFileName = (path: string) => {
    return path.replace(/^.*[\\/]/, '');
  };

  const getStatusBadge = (status: string) => {
    const badges = {
      'pending': 'bg-blue-900/20 text-blue-300 border-blue-600/30',
      'processing': 'bg-yellow-900/20 text-yellow-300 border-yellow-600/30',
      'completed': 'bg-green-900/20 text-green-300 border-green-600/30',
      'failed': 'bg-red-900/20 text-red-300 border-red-600/30',
    };
    return badges[status as keyof typeof badges] || 'bg-gray-900/20 text-gray-300 border-gray-600/30';
  };

  return (
    <div className="p-6 bg-gray-900 min-h-screen max-h-screen overflow-y-auto">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-3xl font-bold text-white mb-2">
            {projectName ? `${projectName} - Transcription Queue` : 'Transcription Queue'}
          </h1>
          <p className="text-gray-400">Manage and monitor your transcription jobs</p>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-blue-900/20 border border-blue-600/30 rounded-lg p-4">
            <div className="text-blue-400 text-sm mb-1">Pending</div>
            <div className="text-3xl font-bold text-blue-300">{pendingCount}</div>
          </div>
          <div className="bg-yellow-900/20 border border-yellow-600/30 rounded-lg p-4">
            <div className="text-yellow-400 text-sm mb-1">Processing</div>
            <div className="text-3xl font-bold text-yellow-300">{processingCount}</div>
          </div>
          <div className="bg-green-900/20 border border-green-600/30 rounded-lg p-4">
            <div className="text-green-400 text-sm mb-1">Completed</div>
            <div className="text-3xl font-bold text-green-300">{completedCount}</div>
          </div>
          <div className="bg-red-900/20 border border-red-600/30 rounded-lg p-4">
            <div className="text-red-400 text-sm mb-1">Failed</div>
            <div className="text-3xl font-bold text-red-300">{failedCount}</div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex gap-3 mb-6">
          <button
            onClick={handleToggleQueueProcessing}
            disabled={pendingCount === 0}
            className="bg-blue-600 hover:bg-blue-700 disabled:bg-gray-700 disabled:cursor-not-allowed text-white px-4 py-2 rounded-lg font-medium transition-colors"
          >
            {isProcessing ? '⏸️ Pause Processing' : '▶️ Start Processing'}
          </button>
          <button
            onClick={handleClearCompleted}
            disabled={false}
            className="bg-gray-700 hover:bg-gray-600 disabled:bg-gray-800 disabled:cursor-not-allowed text-white px-4 py-2 rounded-lg font-medium transition-colors"
          >
                        🗑️ Clear Completed
          </button>
          {pendingCount === 0 && queue.length > 0 && (
            <button
              onClick={() => {
                if (projectId) {
                  navigate(`/projects/${projectId}/batch-transcribe`);
                } else {
                  navigate('/batch-transcribe');
                }
              }}
              className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg font-medium transition-colors"
            >
              ➕ Add Transcriptions
            </button>
          )}
        </div>

        {/* Live Output Window */}
        {(liveOutput || queueCompletionStatus) && (
          <div className="mb-6 bg-gray-950 border border-gray-700 rounded-lg p-3">
            <div className="flex items-center gap-2 mb-2">
              <span className={`text-xs font-mono ${processingCount > 0 ? 'text-green-400 animate-pulse' : 'text-gray-400'}`}>●</span>
              <span className="text-gray-400 text-xs font-semibold uppercase">Live Status</span>
            </div>
            <div className="font-mono text-sm text-gray-300 whitespace-pre-wrap">
              {queueCompletionStatus || liveOutput}
              {processingCount > 0 && !queueCompletionStatus && (
                <span>{'.'.repeat(ellipsisCount)}</span>
              )}
            </div>
          </div>
        )}

        {/* Queue List */}
        <div className="bg-gray-800 border border-gray-700 rounded-lg overflow-hidden max-h-96 overflow-y-auto">
          {queue.length === 0 ? (
            <div className="p-12 text-center">
              <div className="text-6xl mb-4">📋</div>
              <h3 className="text-xl font-semibold text-gray-300 mb-2">No jobs in queue</h3>
              <p className="text-gray-500 mb-6">Add transcription jobs from the Batch Transcribe page</p>
              <button
                onClick={() => {
                  if (projectId) {
                    navigate(`/projects/${projectId}/batch-transcribe`);
                  } else {
                    navigate('/batch-transcribe');
                  }
                }}
                className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-lg font-medium transition-colors inline-block"
              >
                ➕ Add Transcriptions
              </button>
            </div>
          ) : (
            <div className="divide-y divide-gray-700">
              {queue.map(job => (
                <div
                  key={job.id}
                  className={`p-4 transition-colors ${job.status.toLowerCase() === 'completed' || job.status.toLowerCase() === 'failed'
                    ? 'hover:bg-gray-750 cursor-pointer'
                    : 'hover:bg-gray-750'
                  }`}
                  onClick={() => handleJobClick(job)}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <span className={`px-2 py-1 rounded text-xs font-medium border ${getStatusBadge(job.status)}`}>
                          {job.status}
                        </span>
                        <span className="text-white font-medium">{formatFileName(job.audioPath)}</span>
                        {(job.status.toLowerCase() === 'completed' || job.status.toLowerCase() === 'failed') && (
                          <span className="text-xs text-gray-400 italic">Click to view logs</span>
                        )}
                      </div>
                      <div className="text-sm text-gray-400 space-y-1">
                        <div>📄 Output: {job.csvFileName}</div>
                        <div>⏱️ {job.elapsedSeconds ? `Duration: ${formatTime(job.elapsedSeconds)}` : 'Not started'}</div>
                        <div className="text-xs text-gray-500">ID: {job.id.substring(0, 8)}...</div>
                        {job.errorMessage && (
                          <div className="text-red-400 mt-2">❌ Error: {job.errorMessage}</div>
                        )}
                      </div>
                    </div>
                    <div className="ml-4 flex gap-2">
                      {/* Convert button for completed jobs */}
                      {job.status.toLowerCase() === 'completed' && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleNavigateToConvert(job);
                          }}
                          className="text-green-400 hover:text-green-300 text-sm px-3 py-1.5 border border-green-600/50 rounded hover:bg-green-900/20 transition-colors font-medium"
                          title="Go to convert page"
                        >
                                                    📄 Convert
                        </button>
                      )}
                      {/* Requeue button for failed jobs */}
                      {job.status.toLowerCase() === 'failed' && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleRequeueJob(job);
                          }}
                          className="text-blue-400 hover:text-blue-300 text-sm px-3 py-1.5 border border-blue-600/50 rounded hover:bg-blue-900/20 transition-colors font-medium"
                          title="Add back to queue and retry"
                        >
                                                    🔄 Requeue
                        </button>
                      )}
                      {/* Remove button for all jobs */}
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleRemoveJob(job.id);
                        }}
                        className="text-red-400 hover:text-red-300 text-sm px-3 py-1.5 border border-red-600/50 rounded hover:bg-red-900/20 transition-colors font-medium"
                        title="Remove this job from queue"
                      >
                                                ❌ Remove
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Logs Modal */}
      {showLogsModal && selectedJob && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-gray-800 border border-gray-700 rounded-lg max-w-4xl w-full max-h-[80vh] flex flex-col">
            {/* Modal Header */}
            <div className="p-4 border-b border-gray-700 flex items-center justify-between">
              <div>
                <h3 className="text-lg font-semibold text-white">
                                    Job Logs - {formatFileName(selectedJob.audioPath)}
                </h3>
                <div className="flex items-center gap-2 mt-1">
                  <span className={`px-2 py-1 rounded text-xs font-medium border ${getStatusBadge(selectedJob.status)}`}>
                    {selectedJob.status}
                  </span>
                  <span className="text-sm text-gray-400">
                    {selectedJob.elapsedSeconds ? `${formatTime(selectedJob.elapsedSeconds)}` : 'No duration recorded'}
                  </span>
                </div>
              </div>
              <button
                onClick={closeLogsModal}
                className="text-gray-400 hover:text-white text-xl font-bold w-8 h-8 flex items-center justify-center"
              >
                                ×
              </button>
            </div>

            {/* Modal Content */}
            <div className="flex-1 overflow-y-auto p-4">
              {selectedJob.status.toLowerCase() === 'failed' && selectedJob.errorMessage && (
                <div className="mb-4">
                  <h4 className="text-red-400 font-medium mb-2">❌ Error Details:</h4>
                  <div className="bg-red-900/20 border border-red-600/50 rounded p-3 text-red-200">
                    <pre className="whitespace-pre-wrap text-sm">{selectedJob.errorMessage}</pre>
                  </div>
                </div>
              )}

              {selectedJob.output && (
                <div className="mb-4">
                  <h4 className="text-green-400 font-medium mb-2">📋 Command Output:</h4>
                  <div className="bg-gray-900/50 border border-gray-600/50 rounded p-3 text-gray-200">
                    <pre className="whitespace-pre-wrap text-sm font-mono">{selectedJob.output}</pre>
                  </div>
                </div>
              )}

              {/* Job Details */}
              <div>
                <h4 className="text-blue-400 font-medium mb-2">ℹ️ Job Details:</h4>
                <div className="bg-blue-900/20 border border-blue-600/50 rounded p-3 text-blue-200 text-sm">
                  <div className="grid grid-cols-2 gap-2">
                    <div><strong>ID:</strong> {selectedJob.id}</div>
                    <div><strong>Output File:</strong> {selectedJob.csvFileName}</div>
                    <div><strong>Created:</strong> {new Date(selectedJob.createdAt).toLocaleString()}</div>
                    {selectedJob.startedAt && (
                      <div><strong>Started:</strong> {new Date(selectedJob.startedAt).toLocaleString()}</div>
                    )}
                    {selectedJob.completedAt && (
                      <div><strong>Completed:</strong> {new Date(selectedJob.completedAt).toLocaleString()}</div>
                    )}
                    {selectedJob.elapsedSeconds && (
                      <div><strong>Duration:</strong> {formatTime(selectedJob.elapsedSeconds)}</div>
                    )}
                  </div>
                </div>
              </div>

              {(!selectedJob.output && !selectedJob.errorMessage) && (
                <div className="text-center text-gray-400 py-8">
                  <div className="text-4xl mb-2">📄</div>
                  <p>No logs available for this job</p>
                </div>
              )}
            </div>

            {/* Modal Footer */}
            <div className="p-4 border-t border-gray-700 flex justify-end">
              <button
                onClick={closeLogsModal}
                className="bg-gray-700 hover:bg-gray-600 text-white px-4 py-2 rounded-lg font-medium transition-colors"
              >
                                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TranscriptionQueuePage;
